import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import {
  ClipboardCheck, UserCheck, UserX, Users, Save,
  Calendar, Clock, WifiOff,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import {
  PageHeader, AppButton, AppCard, AppCardContent, AppCardHeader, AppCardTitle,
  SearchInput, FormActionBar, ResetButton,
} from "../ui";
import CommonToaster from "../Common/CommonToaster";
import "./attendance.scss";

/**
 * MarkOfflineAttendancePage — Mark attendance for offline/physical classes
 * that happened without an online Zoom meeting.
 *
 * Route: /attendance/offline
 */
const MarkOfflineAttendancePage = ({ basePath = "/attendance" }) => {
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [batches, setBatches] = useState([]);
  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  // Form state
  const [selectedBatch, setSelectedBatch] = useState("");
  const [sessionDate, setSessionDate] = useState(today);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");

  // Load batches on mount
  useEffect(() => {
    const fetchBatches = async () => {
      try {
        const { data } = await apiClient.get("/batches/list?limit=200&fields=name,startTime,endTime,status");
        setBatches((data.batches || []).filter((b) => b.status !== "Completed"));
      } catch {
        setToast({ show: true, message: "Failed to load batches.", type: "danger" });
      }
    };
    fetchBatches();
  }, []);

  // Load students when batch changes
  const fetchStudents = useCallback(async () => {
    if (!selectedBatch) {
      setStudents([]);
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (sessionDate) params.set("date", sessionDate);
      const { data } = await apiClient.get(`/attendance/batch/${selectedBatch}/active-students?${params.toString()}`);
      const mapped = (data.students || []).map((s) => ({
        ...s,
        // Students on approved leave default to absent (saved as "excused").
        manualStatus: s.onLeave ? "absent" : "present",
        note: "",
      }));
      setStudents(mapped);
    } catch {
      setToast({ show: true, message: "Failed to load students.", type: "danger" });
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBatch, sessionDate]);

  useEffect(() => { fetchStudents(); }, [fetchStudents]);

  // Auto-fill times from batch when batch changes
  useEffect(() => {
    if (!selectedBatch) return;
    const batch = batches.find((b) => b._id === selectedBatch);
    if (batch?.startTime) setStartTime(batch.startTime);
    if (batch?.endTime) setEndTime(batch.endTime);
  }, [selectedBatch, batches]);

  const toggleStudent = (studentId, status) => {
    setStudents((prev) =>
      prev.map((s) =>
        String(s._id) === String(studentId) ? { ...s, manualStatus: status } : s
      )
    );
  };

  const handleSave = async () => {
    if (!selectedBatch) {
      setToast({ show: true, message: "Please select a batch.", type: "danger" });
      return;
    }
    if (!sessionDate) {
      setToast({ show: true, message: "Please select a date.", type: "danger" });
      return;
    }
    if (students.length === 0) {
      setToast({ show: true, message: "No students found in this batch.", type: "danger" });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        batchId: selectedBatch,
        sessionDate,
        startTime: startTime || undefined,
        endTime: endTime || undefined,
        students: students.map((s) => ({
          studentId: s._id,
          name: s.name,
          status: s.manualStatus || "absent",
          note: s.note || "",
        })),
      };
      await apiClient.post("/attendance/offline-session", payload);
      setToast({ show: true, message: "Offline attendance saved successfully!", type: "success" });
      setTimeout(() => navigate(basePath), 1200);
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to save attendance.";
      setToast({ show: true, message: msg, type: "danger" });
    }
    setSaving(false);
  };

  // Clear the whole form back to its initial state.
  const handleReset = () => {
    setSelectedBatch("");
    setSessionDate(today);
    setStartTime("");
    setEndTime("");
    setStudents([]);
    setSearchTerm("");
  };

  const filtered = students.filter((s) =>
    !searchTerm || s.name?.toLowerCase().includes(searchTerm.toLowerCase())
      || s.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const presentCount = students.filter((s) => s.manualStatus === "present").length;
  const absentCount = students.filter((s) => s.manualStatus !== "present").length;

  return (
    <div className="attendance-page sl-page has-form-actionbar">
      <PageHeader
        icon={ClipboardCheck}
        title="Mark Offline Attendance"
        description="Record attendance for classes that happened offline (without an online meeting)"
      />

      {/* Batch & Date Selection */}
      <AppCard className="mb-3">
        <AppCardHeader>
          <AppCardTitle>
            <span className="attendance-card-title">
              <Calendar size={18} /> Session Details
            </span>
          </AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          <div className="attendance-offline-form">
            <div className="attendance-offline-form__field">
              <label>Batch <span className="text-danger">*</span></label>
              <select
                className="form-select form-select-sm"
                value={selectedBatch}
                onChange={(e) => {
                  setSelectedBatch(e.target.value);
                  setStartTime("");
                  setEndTime("");
                }}
              >
                <option value="">Select Batch</option>
                {batches.map((b) => (
                  <option key={b._id} value={b._id}>{b.name}</option>
                ))}
              </select>
            </div>
            <div className="attendance-offline-form__field">
              <label>Date <span className="text-danger">*</span></label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={sessionDate}
                onChange={(e) => setSessionDate(e.target.value)}
                max={today}
              />
            </div>
            <div className="attendance-offline-form__field">
              <label><Clock size={14} /> Start Time</label>
              <input
                type="time"
                className="form-control form-control-sm"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </div>
            <div className="attendance-offline-form__field">
              <label><Clock size={14} /> End Time</label>
              <input
                type="time"
                className="form-control form-control-sm"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </div>
          </div>
        </AppCardContent>
      </AppCard>

      {/* Student List */}
      {selectedBatch && (
        <>
          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" /></div>
          ) : students.length === 0 ? (
            <AppCard>
              <AppCardContent>
                <p className="text-center text-muted py-3">No active students found in this batch.</p>
              </AppCardContent>
            </AppCard>
          ) : (
            <>
              {/* Summary */}
              <div className="attendance-summary-row" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
                <div className="attendance-stat-card attendance-stat-card--total">
                  <Users size={20} />
                  <div>
                    <span className="attendance-stat-card__value">{students.length}</span>
                    <span className="attendance-stat-card__label">Total Students</span>
                  </div>
                </div>
                <div className="attendance-stat-card attendance-stat-card--present">
                  <UserCheck size={20} />
                  <div>
                    <span className="attendance-stat-card__value">{presentCount}</span>
                    <span className="attendance-stat-card__label">Present</span>
                  </div>
                </div>
                <div className="attendance-stat-card attendance-stat-card--absent">
                  <UserX size={20} />
                  <div>
                    <span className="attendance-stat-card__value">{absentCount}</span>
                    <span className="attendance-stat-card__label">Absent</span>
                  </div>
                </div>
              </div>

              {/* Student Attendance Card */}
              <AppCard className="attendance-student-card">
                <AppCardHeader>
                  <AppCardTitle>
                    <span className="attendance-card-title">
                      <Users size={18} /> Mark Attendance
                    </span>
                  </AppCardTitle>
                  <SearchInput
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder="Search students..."
                  />
                </AppCardHeader>
                <AppCardContent>
                  {/* Quick actions */}
                  <div className="attendance-quick-actions">
                    <AppButton
                      variant="outline"
                      size="sm"
                      onClick={() => setStudents((prev) => prev.map((s) => ({ ...s, manualStatus: "present" })))}
                    >
                      <UserCheck size={14} /> Mark All Present
                    </AppButton>
                    <AppButton
                      variant="outline"
                      size="sm"
                      onClick={() => setStudents((prev) => prev.map((s) => ({ ...s, manualStatus: "absent" })))}
                    >
                      <UserX size={14} /> Mark All Absent
                    </AppButton>
                  </div>

                  <div className="attendance-student-list">
                    {filtered.map((student) => (
                      <div
                        key={student._id}
                        className={`attendance-student-row ${
                          student.manualStatus === "present"
                            ? "attendance-student-row--present"
                            : "attendance-student-row--absent"
                        }`}
                      >
                        <div className="attendance-student-row__info">
                          <div className="attendance-student-row__name">
                            {student.name}
                            <span className="attendance-badge attendance-badge--manual">
                              <WifiOff size={12} /> Offline
                            </span>
                            {student.onLeave && (
                              <span className="attendance-badge attendance-badge--excused">
                                On leave
                              </span>
                            )}
                          </div>
                          {student.email && (
                            <span className="attendance-student-row__email">{student.email}</span>
                          )}
                        </div>

                        <div className="attendance-student-row__actions">
                          <button
                            type="button"
                            className={`attendance-toggle-btn attendance-toggle-btn--present ${
                              student.manualStatus === "present" ? "active" : ""
                            }`}
                            onClick={() => toggleStudent(student._id, "present")}
                          >
                            <UserCheck size={14} /> Present
                          </button>
                          <button
                            type="button"
                            className={`attendance-toggle-btn attendance-toggle-btn--absent ${
                              student.manualStatus !== "present" ? "active" : ""
                            }`}
                            onClick={() => toggleStudent(student._id, "absent")}
                          >
                            <UserX size={14} /> Absent
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </AppCardContent>
              </AppCard>

            </>
          )}
        </>
      )}

      <FormActionBar>
        <AppButton variant="outline" type="button" onClick={() => navigate(-1)}>
          Cancel
        </AppButton>
        <ResetButton onReset={handleReset} disabled={saving} />
        <AppButton variant="primary" onClick={handleSave} disabled={saving || !selectedBatch || students.length === 0}>
          {saving ? "Saving…" : <><Save size={16} /> Save Offline Attendance</>}
        </AppButton>
      </FormActionBar>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default MarkOfflineAttendancePage;
